// File: src/components/Settings/SettingsPage.jsx
import React, { useState, useContext, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Tabs,
  FormLayout,
  TextField,
  Button,
  Banner,
  SkeletonPage,
  SkeletonBodyText,
  Text,
  Badge,
  BlockStack,
  InlineStack,
  Spinner
} from '@shopify/polaris';
import { AuthContext } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheProvider';
import LayoutWrapper from '../components/Layout/Layout';
import useData from '../hooks/useData';
import { SettingsAPI } from '../warmup/settingsAPI';
import BillingDashboard from '../components/billing/BillingDashboard';
import NotificationPreferences from '../components/Settings/NotificationPreferences';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { token, logout } = useContext(AuthContext);
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();

  // Detect if user is in Shopify embedded context
  const isShopifyEmbedded = !!searchParams.get('host');

  // --- Active site from sessionStorage ---
  const [activeSite, setActiveSite] = useState(() => {
    const id = sessionStorage.getItem('site_id');
    const siteToken = sessionStorage.getItem('site_token');
    const platform = sessionStorage.getItem('site_platform');
    const url = sessionStorage.getItem('site_url');
    return id || siteToken || platform || url ? { id, token: siteToken, platform, url } : null;
  });

  // Refresh once after mount
  React.useEffect(() => {
    const id = sessionStorage.getItem('site_id');
    const siteToken = sessionStorage.getItem('site_token');
    const platform = sessionStorage.getItem('site_platform');
    const url = sessionStorage.getItem('site_url');
    setActiveSite(id || siteToken || platform || url ? { id, token: siteToken, platform, url } : null);
  }, []);

  // Also refresh when user switches sites elsewhere in-app (polling)
  React.useEffect(() => {
    const i = setInterval(() => {
      const id = sessionStorage.getItem('site_id');
      const siteToken = sessionStorage.getItem('site_token');
      const platform = sessionStorage.getItem('site_platform');
      const url = sessionStorage.getItem('site_url');
      const as = id || siteToken || platform || url ? { id, token: siteToken, platform, url } : null;
      setActiveSite(prev =>
        JSON.stringify(prev) === JSON.stringify(as) ? prev : as
      );
    }, 1000);
    return () => clearInterval(i);
  }, []);

  // Normalize platform
  const platform = (activeSite?.platform || '').toLowerCase().replace(/[\s_-]+/g, '');

  // Determine if this is a Shopify or BigCommerce user (no "Add Site" for them)
  const isShopifyOrBigCommerce = useMemo(() => {
    if (isShopifyEmbedded) return true;
    if (platform.includes('shopify')) return true;
    if (platform.includes('bigcommerce') || platform === 'bc' || platform === 'bigc') return true;
    return false;
  }, [platform, isShopifyEmbedded]);

  // Build tabs based on platform
  // Shopify/BigCommerce: Profile → Integrations → Notifications → Billing (NO sites tab)
  // WooCommerce/Custom: Profile → Connected Sites → Integrations → Notifications → Billing
  const tabs = useMemo(() => {
    if (isShopifyOrBigCommerce) {
      return [
        { id: 'profile', content: 'Profile' },
        { id: 'integrations', content: 'Integrations' },
        { id: 'notifications', content: 'Notifications' },
        { id: 'billing', content: 'Billing & Payments' },
      ];
    } else {
      return [
        { id: 'profile', content: 'Profile' },
        { id: 'sites', content: 'Connected Sites' },
        { id: 'integrations', content: 'Integrations' },
        { id: 'notifications', content: 'Notifications' },
        { id: 'billing', content: 'Billing & Payments' },
      ];
    }
  }, [isShopifyOrBigCommerce]);

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const activeTab = tabs[activeTabIndex].id;

  React.useEffect(() => {
    const isBillingPath = pathname.toLowerCase() === '/settings/billing';
    const tabParam = new URLSearchParams(search).get('tab');

    if (isBillingPath) {
      const idx = tabs.findIndex(t => t.id === 'billing');
      if (idx >= 0) setActiveTabIndex(idx);
      return;
    }

    if (tabParam) {
      const idx = tabs.findIndex(t => t.id === tabParam);
      if (idx >= 0) setActiveTabIndex(idx);
    }
  }, [pathname, search, tabs]);

  const cache = useDataCache();
  const settingsAPI = useMemo(() => new SettingsAPI(cache, token, API), [cache, token]);

  // Build integrations path based on platform
  const integrationsPath = useMemo(() => {
    const siteId = activeSite?.id || sessionStorage.getItem('site_id');
    const siteToken = activeSite?.token || sessionStorage.getItem('site_token');
    const siteUrl = activeSite?.url || sessionStorage.getItem('site_url');

    // For Shopify embedded, preserve the shop/host params
    if (isShopifyEmbedded) {
      const shop = searchParams.get('shop');
      const host = searchParams.get('host');
      return `/integrations?shop=${shop}&host=${host}`;
    }

    if (platform.includes('bigcommerce') || platform === 'bc' || platform === 'bigc') {
      return '/integrations/bigcommerce';
    }
    if (platform.includes('woocommerce') || platform === 'woo' || platform === 'wc') {
      return '/connect-woocommerce';
    }
    if (platform.includes('shopify')) {
      return '/integrations';
    }
    if (platform.includes('custom') || !platform) {
      return `/install-custom-script?siteId=${siteId}&siteToken=${encodeURIComponent(siteToken)}&shopDomain=${encodeURIComponent(siteUrl || '')}`;
    }
    return '/integrations';
  }, [platform, activeSite, isShopifyEmbedded, searchParams]);

  // State for save operations
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState(null);
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false);
  const [deletingSite, setDeletingSite] = useState(null);
  const [siteDeleteError, setSiteDeleteError] = useState(null);
  const [siteDeleteSuccess, setSiteDeleteSuccess] = useState(false);

  const handleTabChange = useCallback((selectedIndex) => {
    setActiveTabIndex(selectedIndex);
  }, []);

  // --- Data Fetching ---

  // 1. Profile Data
  const { data: profile, loading: profileLoading, error: profileError, refresh: refreshProfile } = useData(
    `userProfile:${token}`,
    async () => {
      if (!token) return Promise.resolve(null);
      const res = await fetch(`${API}/api/user/profile`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch profile.');
      return res.json();
    },
    { deps: [token] }
  );
  const [name, setName] = useState(profile?.name || '');
  const [email, setEmail] = useState(profile?.email || '');

  React.useEffect(() => {
    if (profile) {
      setName(profile.name || '');
      setEmail(profile.email || '');
    }
  }, [profile]);

  // 2. Connected Sites (only for WooCommerce/Custom users)
  const { data: sites = [], loading: sitesLoading, error: sitesError } = useData(
    `sites:${token}`,
    async () => {
      const res = await fetch(`${API}/api/sites`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch sites.');
      const json = await res.json();
      if (Array.isArray(json)) return json;
      if (Array.isArray(json?.sites)) return json.sites;
      if (Array.isArray(json?.data)) return json.data;
      return [];
    },
    { deps: [token], isLazy: activeTab !== 'sites' || isShopifyOrBigCommerce }
  );

  // 3. GA4 Integration Status - use same endpoint/cache as other pages
  const { data: gaStatusData, loading: gaLoading, error: gaError } = useData(
    `ga4Status:${token}`,
    async () => {
      const res = await fetch(`${API}/integrations/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return { connected: false };
      return res.json();
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token] }
  );
  

  // --- Event Handlers ---

  const saveProfile = useCallback(async () => {
    if (!token) return;
    setSavingProfile(true);
    setProfileSaveError(null);
    setProfileSaveSuccess(false);

    try {
      await settingsAPI.saveProfile({ name, email });
      setProfileSaveSuccess(true);
      setTimeout(() => setProfileSaveSuccess(false), 3000);
      refreshProfile();
    } catch (err) {
      setProfileSaveError(err.message);
    } finally {
      setSavingProfile(false);
    }
  }, [settingsAPI, name, email, refreshProfile, token]);

  const disconnectSite = useCallback(async (siteId) => {
    if (!token) return;
    setDeletingSite(siteId);
    setSiteDeleteError(null);
    setSiteDeleteSuccess(false);

    try {
      await settingsAPI.disconnectSite(siteId);
      setSiteDeleteSuccess(true);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      setSiteDeleteError(err.message);
      setDeletingSite(null);
    }
  }, [settingsAPI, token]);

  // --- Render ---

  if (profileLoading) {
    return (
      <LayoutWrapper>
        <SkeletonPage title="Account Settings" primaryAction>
          <Layout>
            <Layout.Section>
              <Card sectioned><SkeletonBodyText lines={1} /></Card>
            </Layout.Section>
            <Layout.Section>
              <Card sectioned>
                <Spinner size="small" />
                <SkeletonBodyText lines={3} />
              </Card>
            </Layout.Section>
          </Layout>
        </SkeletonPage>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <Page
        title="Account Settings"
        backAction={{ content: 'Back to Dashboard', onAction: () => navigate('/Dashboard') }}
      >
        <Layout>
          <Layout.Section>
            <Tabs tabs={tabs} selected={activeTabIndex} onSelect={handleTabChange} />
          </Layout.Section>

          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <Layout.Section>
              <Card sectioned title="Profile">
                {profileError && <Banner status="critical">{typeof profileError === 'string' ? profileError : profileError?.message || 'Failed to load profile'}</Banner>}
                {profileSaveError && <Banner status="critical">{typeof profileSaveError === 'string' ? profileSaveError : profileSaveError?.message || 'Failed to save profile'}</Banner>}
                {profileSaveSuccess && <Banner status="success">Profile saved successfully!</Banner>}
                <FormLayout>
                  <TextField
                    label="Name"
                    value={name}
                    onChange={setName}
                    placeholder="Your Name"
                    type="text"
                    disabled={savingProfile}
                  />
                  <TextField
                    label="Email"
                    value={email}
                    onChange={setEmail}
                    placeholder="your.email@example.com"
                    type="email"
                    disabled={savingProfile}
                  />
                  <Button primary onClick={saveProfile} loading={savingProfile}>
                    Save Profile
                  </Button>
                </FormLayout>
              </Card>
              <Card sectioned title="Security">
                <Button destructive onClick={logout}>Log Out</Button>
              </Card>
            </Layout.Section>
          )}

          {/* CONNECTED SITES TAB - Only for WooCommerce/Custom users */}
          {activeTab === 'sites' && !isShopifyOrBigCommerce && (
            <Layout.Section>
              <Card sectioned title="Connected Sites">
                {sitesLoading ? (
                  <Text>
                    <Spinner size="small" /> Loading sites...
                  </Text>
                ) : sitesError ? (
                  <Banner status="critical">{typeof sitesError === 'string' ? sitesError : sitesError?.message || 'Failed to load sites'}</Banner>
                ) : (
                  <>
                    {siteDeleteError && (
                      <Banner status="critical" onDismiss={() => setSiteDeleteError(null)}>
                        {typeof siteDeleteError === 'string' ? siteDeleteError : siteDeleteError?.message || 'Failed to disconnect site'}
                      </Banner>
                    )}
                    {siteDeleteSuccess && (
                      <Banner status="success" onDismiss={() => setSiteDeleteSuccess(false)}>
                        Site successfully disconnected!
                      </Banner>
                    )}
                    {Array.isArray(sites) && sites.length === 0 ? (
                      <>
                        <Text as="p" variant="bodyMd">No sites are currently connected to your account.</Text>
                        <div style={{ marginTop: '20px' }}>
                          <Button primary onClick={() => navigate('/sites')}>
                            Add Your First Site
                          </Button>
                        </div>
                      </>
                    ) : (
                      <BlockStack vertical spacing="tight">
                        {(Array.isArray(sites) ? sites : []).map(site => (
                          <Card key={site.id} sectioned>
                            <InlineStack alignment="center" distribution="equalSpacing">
                              <Text variant="bodyLg" as="h3">{site.name} (<Text color="subdued" as="span">{site.url}</Text>)</Text>
                              <InlineStack spacing="base">
                                <Badge status="success">Connected</Badge>
                                <Button
                                  size="slim"
                                  destructive
                                  onClick={() => disconnectSite(site.id)}
                                  loading={deletingSite === site.id}
                                >
                                  Disconnect
                                </Button>
                              </InlineStack>
                            </InlineStack>
                          </Card>
                        ))}
                        <div style={{ marginTop: '20px' }}>
                          <Button onClick={() => navigate('/sites')}>
                            Add Another Site
                          </Button>
                        </div>
                      </BlockStack>
                    )}
                  </>
                )}
              </Card>
            </Layout.Section>
          )}

          {/* INTEGRATIONS TAB */}
          {activeTab === 'integrations' && (
            <Layout.Section>
              <Card sectioned title="Google Analytics (GA4)">
                {gaError && <Banner status="critical">{typeof gaError === 'string' ? gaError : gaError?.message || 'Failed to load GA4 status'}</Banner>}
                {gaLoading ? (
  <BlockStack vertical alignment="center">
    <Spinner size="small" />
    <Text>Checking GA4 connection status...</Text>
  </BlockStack>
) : gaStatusData?.connected ? (
  <BlockStack vertical spacing="base">
    <Text>
      GA4 Status: <Badge status="success">Connected</Badge>
    </Text>

    <InlineStack spacing="base">
      <Button onClick={() => navigate(integrationsPath)}>
        Manage Integrations
      </Button>
    </InlineStack>
  </BlockStack>
) : (
  <BlockStack vertical spacing="base">
    <Text>GA4 Status: <Badge status="info">Not Connected</Badge></Text>
    <Text color="subdued" as="p">
      Connect your Google Analytics 4 property to unlock advanced insights (optional).
    </Text>
    <InlineStack spacing="base">
      <Button primary onClick={() => navigate(integrationsPath)}>
        Connect Google Analytics
      </Button>
    </InlineStack>
  </BlockStack>
)}
              </Card>
            </Layout.Section>
          )}

          {/* NOTIFICATIONS TAB */}
          {activeTab === 'notifications' && (
            <Layout.Section>
              <NotificationPreferences token={token} />
            </Layout.Section>
          )}

          {/* BILLING TAB */}
          {activeTab === 'billing' && (
            <Layout.Section>
              <BillingDashboard userId={profile?.id} />
            </Layout.Section>
          )}

        </Layout>
      </Page>
    </LayoutWrapper>
  );
}